import store from '@/store';
import { omit } from 'lodash';
import { hasLapChildData } from './lapChild';

const KEY = 'form.passengers';
const LAP_CHILD_CACHE_KEY = 'form.lapChild';

/**
 * Strip lap-child meta keys from a passenger before serializing to localStorage.
 * Lap-child PII (name, doc, DOB) is per-trip data that must not survive
 * across searches. Otherwise, a 2-adult search after a prior 1-adult+1-child
 * purchase hydrates the lap-child sub-form for an adult who never asked
 * for one.
 * The UI-only `lapChildExpanded` flag is also stripped so the form-expansion
 * state stays session-scoped.
 * @param {Object} passenger - Passenger object with optional `meta` field.
 * @returns {Object} Same passenger, with `meta.lapChild` and `meta.lapChildExpanded` removed.
 */
const stripLapChild = (passenger) => {
  if (!passenger?.meta) return passenger;
  if (!passenger.meta.lapChild && passenger.meta.lapChildExpanded === undefined) return passenger;

  return { ...passenger, meta: omit(passenger.meta, ['lapChild', 'lapChildExpanded']) };
};

/**
 * Write the lap-child entries of the current passengers array to the
 * sessionStorage cache.
 * The cache lives in sessionStorage (tab-scoped) so the data survives
 * "back to results, search the same mix again" but dies when the tab closes.
 * Cleared explicitly inside getStoredPassengers when the new search drops
 * `child_in_arms`, so a no-child interlude erases prior lap-child input.
 * @param {Array<Object>} passengers - Passenger objects with optional `meta.lapChild`.
 * @returns {void}
 */
const writeLapChildCache = (passengers) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  const cache = passengers
    .map((passenger, index) => {
      if (!hasLapChildData(passenger?.meta?.lapChild)) return null;

      return { index, lapChild: passenger.meta.lapChild };
    })
    .filter(Boolean);
  if (cache.length === 0) {
    window.sessionStorage.removeItem(LAP_CHILD_CACHE_KEY);
    return;
  }
  window.sessionStorage.setItem(LAP_CHILD_CACHE_KEY, JSON.stringify(cache));
};

/**
 * Read the lap-child sessionStorage cache as an array of `{index, lapChild}`
 * entries. Returns an empty array when sessionStorage is unavailable or the
 * cached JSON cannot be parsed.
 * @returns {Array<{index: number, lapChild: Object}>} Cached lap-child entries.
 */
const readLapChildCache = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return [];
  try {
    return JSON.parse(window.sessionStorage.getItem(LAP_CHILD_CACHE_KEY) || '[]');
  } catch (err) {
    return [];
  }
};

/**
 * Remove the lap-child sessionStorage cache entry. No-op when sessionStorage
 * is unavailable.
 * @returns {void}
 */
const clearLapChildCache = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  window.sessionStorage.removeItem(LAP_CHILD_CACHE_KEY);
};

/**
 * Re-attach a cached lap-child block onto a passenger when the cache has an
 * entry for the same index. Used to restore the lap-child sub-form values
 * after the passenger array has been hydrated from localStorage (which itself
 * does not carry lap-child PII).
 * @param {Object} passenger - Passenger object with optional `meta` field.
 * @param {number} index - Position of the passenger in the current form array.
 * @param {Array<{index: number, lapChild: Object}>} cache - Lap-child cache entries from readLapChildCache.
 * @returns {Object} Passenger with `meta.lapChild` re-attached when a matching cache entry exists; otherwise the passenger unchanged.
 */
const restoreLapChildIntoPassenger = (passenger, index, cache) => {
  const cached = cache.find((entry) => entry.index === index);
  if (!cached) return passenger;
  return {
    ...passenger,
    meta: { ...(passenger.meta || {}), lapChild: cached.lapChild },
  };
};

/**
 * Get the passengers stored in the local storage
 * @param {Object} featuresFromParams - The features object from the params
 * @returns
 */
export function getStoredPassengers(featuresFromParams) {
  const features = featuresFromParams ?? store.getState().whitelabelConfig.features;
  if (!features.STORE_PASSENGERS_FORM) return [];
  const passengers = JSON.parse(
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(KEY) || '[]'
      : '[]',
  );
  const currentChildInArms = Number(
    store.getState().search?.get?.('passengers')?.toJS?.()?.child_in_arms || 0,
  );
  let lapChildCache = [];
  if (currentChildInArms > 0) {
    /**
     * Drop cache entries whose passenger index exceeds the URL-specified
     * child_in_arms count. Without this trim, a prior search with a higher
     * count leaves stale entries that restore phantom lap children on
     * adults the current URL does not cover.
     */
    lapChildCache = readLapChildCache().filter((entry) => entry.index < currentChildInArms);
  } else {
    clearLapChildCache();
  }
  return passengers.map((passenger, index) => {
    const merged = restoreLapChildIntoPassenger(stripLapChild(passenger), index, lapChildCache);
    return {
      ...omit(merged, ['id', 'wantsIncomingInsurance', 'wantsOutgoingInsurance', 'document']),
      canLoggedRemove: true,
    };
  });
}

/**
 * Set the passengers in the local storage
 * @param {Array} passengers - The passengers
 */
export function setStoredPassenger(passengers) {
  writeLapChildCache(passengers);
  passengers = passengers.map((passenger) => {
    return omit(stripLapChild(passenger), [
      'id',
      'wantsIncomingInsurance',
      'wantsOutgoingInsurance',
      'document',
    ]);
  });

  const jsonPassengers = JSON.stringify(passengers);
  if (typeof window !== 'undefined' && window.localStorage)
    window.localStorage.setItem(KEY, jsonPassengers);
}
